import React, { useEffect, useRef, useState } from "react";
import { ImageUp, Info, Trash, X } from "lucide-react";
import {
  PRODUCT_STATUSES,
  STORAGE_CONDITIONS,
  formStyles,
} from "../../../components/Inventory-Stocks & Orders/Admin/Products/catalogConstants";
import { SearchableDropdown } from "../../SearchableDropdown";

interface BasicDetailsProps {
  data: any;
  onChange: (field: string, value: any) => void;
  onImageChange: (
    type: "main" | "sub",
    file: File | null,
    index?: number
  ) => void;
  onRemoveImage: (type: "main" | "sub", index?: number) => void;
  isEdit: boolean;
  onUpdate?: () => void;
  isLoading?: boolean;
  marketerOptions: { id: string; marketerName: string }[];
  scheduleOptions: { id: number; name: string }[];
}

/** Yes / No radio pair, used for every boolean on the product master. */
function BooleanField({
  name,
  label,
  value,
  onChange,
  hint,
}: {
  name: string;
  label: string;
  value: boolean | null;
  onChange: (value: boolean) => void;
  hint?: string;
}) {
  return (
    <div>
      <label className={formStyles.lableStyle}>{label}</label>
      <div className="flex gap-4 mt-1">
        {[true, false].map((option) => (
          <label
            key={String(option)}
            className="flex items-center gap-2 text-sm text-[#3F5260] font-medium cursor-pointer"
          >
            <input
              type="radio"
              name={name}
              checked={value === option}
              onChange={() => onChange(option)}
              className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
            />
            {option ? "Yes" : "No"}
          </label>
        ))}
      </div>
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

/**
 * Everything on product_mstr, in one place, plus the product images.
 *
 * `isMedicine` is set here and decides the rest of the form: it selects the
 * medicine salt and attribute steps over the non-medicine ones, and picks which
 * category tree (MEDICINE or NON_MEDICINE) the classification pickers read.
 */
export const BasicDetailsTab: React.FC<BasicDetailsProps> = ({
  data,
  onChange,
  onImageChange,
  onRemoveImage,
  isEdit,
  onUpdate,
  isLoading,
  marketerOptions,
  scheduleOptions,
}) => {
  const [viewingImage, setViewingImage] = useState<{
    type: "main" | "sub";
    index?: number;
    title: string;
  } | null>(null);

  const [marketerSearchText, setMarketerSearchText] = useState("");
  const [showMarketerSuggestions, setShowMarketerSuggestions] = useState(false);
  const marketerDropdownRef = useRef<HTMLDivElement>(null);

  const selectedMarketerName = marketerOptions.find(
    (option) => option.id === data.marketerId
  )?.marketerName;

  /**
   * The box shows the stored marketer's name once the options have loaded, so
   * an existing product does not look as though it has no marketer. It only
   * writes when the box is empty, to leave whatever is being typed alone.
   */
  useEffect(() => {
    if (selectedMarketerName && !marketerSearchText) {
      setMarketerSearchText(selectedMarketerName);
    }
  }, [selectedMarketerName, marketerSearchText]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        marketerDropdownRef.current &&
        !marketerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowMarketerSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="space-y-6">
      {/* ------------------------------------------------- Identity */}
      <section className={formStyles.sectionCard}>
        <h4 className={`${formStyles.sectionTitle} mb-4`}>Identity</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={formStyles.lableStyle}>
              Product Name<span className="text-red-500 ml-0.5">*</span>
            </label>
            <input
              type="text"
              value={data.name}
              onChange={(e) => onChange("name", e.target.value)}
              className={formStyles.inputStyle}
              placeholder="Enter Product Name"
            />
          </div>

          <div className="md:col-span-2">
            <label className={formStyles.lableStyle}>Description</label>
            <textarea
              value={data.description}
              onChange={(e) => onChange("description", e.target.value)}
              className={`${formStyles.inputStyle} h-24 resize-none`}
              placeholder="Enter Product Description"
            />
          </div>

          <div>
            <label className={formStyles.lableStyle}>Brand Name</label>
            <input
              type="text"
              value={data.brandName}
              onChange={(e) => onChange("brandName", e.target.value)}
              className={formStyles.inputStyle}
              placeholder="e.g. Crocin"
            />
          </div>

          <div>
            <label className={formStyles.lableStyle}>Marketer</label>
            {/* Typed rather than scrolled: the marketer list runs long. */}
            <SearchableDropdown
              placeholder="Type a marketer name"
              searchText={marketerSearchText}
              setSearchText={(text) => {
                setMarketerSearchText(text);
                if (!text.trim()) onChange("marketerId", "");
              }}
              suggestionsVisible={showMarketerSuggestions}
              setSuggestionsVisible={setShowMarketerSuggestions}
              options={marketerOptions.map((option) => ({
                id: option.id,
                label: option.marketerName,
              }))}
              onSelect={(selected) => {
                onChange("marketerId", selected.id);
                setMarketerSearchText(selected.label);
              }}
              dropdownRef={marketerDropdownRef}
            />
            {data.marketerId && (
              <p className="text-xs text-emerald-600 mt-1">
                Selected: {selectedMarketerName || data.marketerId}
              </p>
            )}
          </div>

          <div>
            <label className={formStyles.lableStyle}>Country of Origin</label>
            <input
              type="text"
              value={data.countryOfOrigin}
              onChange={(e) => onChange("countryOfOrigin", e.target.value)}
              className={formStyles.inputStyle}
              placeholder="e.g. India"
            />
          </div>

          <div>
            <label className={formStyles.lableStyle}>
              Units Per Strip / Pack
            </label>
            <input
              type="number"
              min={1}
              value={data.tabletPerStripe}
              onChange={(e) =>
                onChange("tabletPerStripe", Number(e.target.value))
              }
              className={formStyles.inputStyle}
            />
            <p className="text-xs text-gray-400 mt-1">
              Drives every stock and order quantity conversion.
            </p>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------- Classification flag */}
      <section className="border border-emerald-200 bg-emerald-50/40 rounded-xl p-4">
        <div className="flex items-start gap-2 mb-3">
          <Info className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
          <p className="text-xs text-emerald-800">
            Is Medicine decides the rest of this form - which salt and attribute
            sections appear, and whether the category pickers read the Medicine
            or Non Medicine tree. Changing it clears the category mapping.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <BooleanField
            name="isMedicine"
            label="Is Medicine"
            value={data.isMedicine}
            onChange={(value) => onChange("isMedicine", value)}
          />
          <BooleanField
            name="isRxRequired"
            label="Rx Required"
            value={data.isRxRequired}
            onChange={(value) => onChange("isRxRequired", value)}
          />
          {data.isMedicine && (
            <div>
              <label className={formStyles.lableStyle}>Medicine Schedule</label>
              <select
                value={data.medicineScheduleId}
                onChange={(e) => onChange("medicineScheduleId", e.target.value)}
                className={formStyles.inputStyle}
              >
                <option value="">Not scheduled</option>
                {scheduleOptions.map((option) => (
                  <option key={option.id} value={String(option.id)}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </section>

      {/* ------------------------------------------------- Availability */}
      <section className={formStyles.sectionCard}>
        <h4 className={`${formStyles.sectionTitle} mb-4`}>
          Availability &amp; Handling
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <BooleanField
            name="isPublished"
            label="Published"
            value={data.isPublished}
            onChange={(value) => onChange("isPublished", value)}
          />
          <BooleanField
            name="isActive"
            label="Active"
            value={data.isActive}
            onChange={(value) => onChange("isActive", value)}
          />
          <BooleanField
            name="clinicOnly"
            label="Clinic Only"
            value={data.clinicOnly}
            onChange={(value) => onChange("clinicOnly", value)}
            hint="Hidden from the patient storefront."
          />
          <BooleanField
            name="isReturnable"
            label="Returnable"
            value={data.isReturnable}
            onChange={(value) => onChange("isReturnable", value)}
          />
          <BooleanField
            name="discretePackaging"
            label="Discrete Packaging"
            value={data.discretePackaging}
            onChange={(value) => onChange("discretePackaging", value)}
          />
          <BooleanField
            name="coldChainRequired"
            label="Cold Chain Required"
            value={data.coldChainRequired}
            onChange={(value) => onChange("coldChainRequired", value)}
          />

          <div>
            <label className={formStyles.lableStyle}>Catalogue Status</label>
            <select
              value={data.status}
              onChange={(e) => onChange("status", e.target.value)}
              className={formStyles.inputStyle}
            >
              <option value="">Not set</option>
              {PRODUCT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status === "UnClassified" ? "Unclassified" : status}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className={formStyles.lableStyle}>
              Storage Instructions
            </label>
            <input
              type="text"
              list="storage-conditions"
              value={data.storageInstructions}
              onChange={(e) => onChange("storageInstructions", e.target.value)}
              className={formStyles.inputStyle}
              placeholder="e.g. Room Temperature (below 30°C)"
            />
            <datalist id="storage-conditions">
              {STORAGE_CONDITIONS.map((condition) => (
                <option key={condition} value={condition} />
              ))}
            </datalist>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------- Images */}
      <section className={formStyles.sectionCard}>
        <h4 className={`${formStyles.sectionTitle} mb-4`}>Product Images</h4>
        <p className="text-sm text-gray-500 mb-3 font-medium">Main Image</p>
        <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-white relative group">
          {data.mainImagePreview ? (
            <div
              onClick={() =>
                setViewingImage({ type: "main", title: "Main Product Image" })
              }
              className="w-full h-full cursor-pointer relative group flex items-center justify-center"
              title="Click to view or edit image"
            >
              <img
                src={data.mainImagePreview}
                alt="Main"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="text-white text-xs font-medium px-2 py-1 bg-black/60 rounded-md">
                  Click to View
                </span>
              </div>
            </div>
          ) : (
            <label className="cursor-pointer w-full h-full flex flex-col items-center justify-center hover:bg-gray-50">
              <ImageUp className="w-8 h-8 text-gray-400 mb-2" />
              <span className="text-xs text-gray-500">Upload Main</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    onImageChange("main", e.target.files[0]);
                  }
                }}
              />
            </label>
          )}
        </div>

        <p className="text-sm text-gray-500 mt-6 mb-3 font-medium">
          Sub Images (Up to 3)
        </p>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className="w-full aspect-square border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-white relative group"
            >
              {data.subImagePreviews[index] ? (
                <div
                  onClick={() =>
                    setViewingImage({
                      type: "sub",
                      index,
                      title: `Sub Product Image ${index + 1}`,
                    })
                  }
                  className="w-full h-full cursor-pointer relative group flex items-center justify-center"
                  title="Click to view or edit image"
                >
                  <img
                    src={data.subImagePreviews[index]!}
                    alt={`Sub ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-xs font-medium px-2 py-1 bg-black/60 rounded-md">
                      View
                    </span>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer w-full h-full flex flex-col items-center justify-center hover:bg-gray-50">
                  <ImageUp className="w-6 h-6 text-gray-400 mb-1" />
                  <span className="text-xs text-gray-500">Upload</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        onImageChange("sub", e.target.files[0], index);
                      }
                    }}
                  />
                </label>
              )}
            </div>
          ))}
        </div>
      </section>

      {isEdit && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onUpdate}
            disabled={isLoading}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition disabled:opacity-50"
          >
            {isLoading ? "Updating..." : "Update Basic Details"}
          </button>
        </div>
      )}

      {/* Image preview & replace dialog */}
      {viewingImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="relative bg-white rounded-2xl max-w-2xl w-full p-6 flex flex-col gap-4 shadow-2xl max-h-[90vh]">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="text-lg font-semibold text-gray-800">
                {viewingImage.title}
              </h3>
              <button
                type="button"
                onClick={() => setViewingImage(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex items-center justify-center bg-gray-50 rounded-xl p-4 overflow-hidden min-h-[250px] max-h-[55vh]">
              <img
                src={
                  viewingImage.type === "main"
                    ? data.mainImagePreview
                    : data.subImagePreviews[viewingImage.index!]
                }
                alt={viewingImage.title}
                className="max-h-[50vh] w-auto max-w-full object-contain rounded-md"
              />
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <button
                type="button"
                onClick={() => {
                  onRemoveImage(viewingImage.type, viewingImage.index);
                  setViewingImage(null);
                }}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1.5"
              >
                <Trash size={16} />
                Remove Image
              </button>

              <div className="flex items-center gap-3">
                <label className="cursor-pointer px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg flex items-center gap-2">
                  <ImageUp size={16} />
                  Update Image
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        onImageChange(
                          viewingImage.type,
                          e.target.files[0],
                          viewingImage.index
                        );
                      }
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setViewingImage(null)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
